'use client';

import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Lock, Eye, Database, Cookie, Mail, CheckCircle } from 'lucide-react';
import Footer from '@/components/Footer';


export default function PrivacidadePage() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-emerald-200 selection:text-emerald-900">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-20 text-center space-y-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 px-6 py-3 rounded-2xl text-white font-black uppercase tracking-tighter italic transition-all hover:-translate-x-1 active:scale-95 mb-8 group shadow-lg"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Voltar para o Início
          </Link>
          <h1 className="text-3xl md:text-5xl font-black tracking-tighter uppercase italic">
            Privacidade <br className="md:hidden" />
            <span className="text-emerald-200">Catanduvas PR</span>
          </h1>
          <p className="text-emerald-100 text-lg md:text-xl font-medium max-w-2xl mx-auto">
            Sua privacidade é nossa prioridade. Saiba como protegemos e tratamos seus dados pessoais.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 -mt-8 pb-20">
        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-2xl border border-slate-100 space-y-12">
          
          {/* Introdução */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-100 p-3 rounded-2xl">
                <ShieldCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">1. Compromisso com a Privacidade</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                O portal <strong>CatanduvasPR.com.br</strong>, administrado pelo <strong>Estúdio Mídia Publicidades</strong>, está comprometido com a proteção da privacidade e dos dados pessoais de seus usuários, em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD)</strong>.
              </p>
              <p>
                Esta política descreve quais informações coletamos, como as utilizamos e quais são os seus direitos como titular dos dados.
              </p>
            </div>
          </section>

          {/* Coleta de Dados */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-2xl">
                <Database className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">2. Coleta de Informações</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                Coletamos informações de duas formas principais:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Informações fornecidas por você:</strong> Nome, e-mail, telefone e mensagem enviados através do nosso formulário de contato.</li>
                <li><strong>Informações automáticas:</strong> Dados de navegação (endereço IP, tipo de navegador, páginas visitadas) coletados através de cookies e ferramentas de análise (como o Google Analytics).</li>
              </ul>
            </div>
          </section>

          {/* Finalidade */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-100 p-3 rounded-2xl">
                <Eye className="w-8 h-8 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">3. Uso das Informações</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                As informações coletadas são utilizadas exclusivamente para:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Responder às suas solicitações enviadas pelo formulário de contato;</li>
                <li>Melhorar a experiência de navegação e o conteúdo do portal;</li>
                <li>Garantir a segurança técnica do portal;</li>
                <li>Cumprir obrigações legais ou regulatórias.</li>
              </ul>
              <p className="mt-4 font-bold text-slate-800">
                O CatanduvasPR.com.br NÃO vende, aluga ou compartilha seus dados pessoais com terceiros para fins de marketing.
              </p>
            </div>
          </section>

          {/* Cookies */}
          <section className="space-y-6 p-8 bg-emerald-50 rounded-[2rem] border border-emerald-100">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-600 p-3 rounded-2xl">
                <Cookie className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">4. Política de Cookies</h2>
            </div>
            <div className="text-slate-600 font-medium leading-relaxed space-y-4">
              <p>
                Utilizamos cookies para entender como os usuários interagem com o portal. Cookies são pequenos arquivos de texto armazenados no seu dispositivo.
              </p>
              <p>
                Você pode desativar os cookies nas configurações do seu navegador a qualquer momento, embora isso possa afetar algumas funcionalidades do portal.
              </p>
            </div>
          </section>

          {/* Segurança */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-3 rounded-2xl">
                <Lock className="w-8 h-8 text-slate-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">5. Segurança dos Dados</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                Adotamos medidas técnicas e administrativas para proteger seus dados contra acessos não autorizados, perda, destruição ou alteração. O portal utiliza criptografia SSL (HTTPS) em todas as suas páginas.
              </p>
            </div>
          </section>

          {/* Direitos do Usuário */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-3 rounded-2xl">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">6. Seus Direitos</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                Como titular dos dados, você tem o direito de solicitar o acesso, a correção ou a exclusão de suas informações pessoais a qualquer momento. Para isso, basta entrar em contato conosco através do e-mail <strong>contato@estudiomidia.com.br</strong>.
              </p>
            </div>
          </section>

          {/* Contato */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-3 rounded-2xl">
                <Mail className="w-8 h-8 text-slate-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">7. Contato</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                Se você tiver qualquer dúvida sobre esta Política de Privacidade, entre em contato conosco pelo e-mail: <strong>contato@estudiomidia.com.br</strong>.
              </p>
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
}
